import { useMemo } from 'react';
import PreviewDetailDrawer, {
  Container,
  SectionInfo,
  InfoRow,
} from 'src/careflow/common/PreviewDetailDrawer';
import { useTag } from '../hooks/useTags';
import { CircularProgress, Box, Button } from '@mui/material';
import moment from 'moment';

interface TagDetailDrawerProps {
  open: boolean;
  tagId: string;
  onClose: () => void;
  onEdit: (tagId: string) => void;
}

export default function TagDetailDrawer({
  open,
  tagId,
  onClose,
  onEdit,
}: TagDetailDrawerProps) {
  const { data: tag, isLoading } = useTag(tagId);

  const tagDetails = useMemo(() => {
    if (!tag) return null;

    const concepts = tag.compose?.include?.[0]?.concept || [];
    const firstConcept = concepts[0];
    const system = tag.compose?.include?.[0]?.system || '';
    const codeSystem = system.split('/').pop() || '';

    // Extract metadata from extensions
    const getExtensionValue = (url: string) => {
      return tag.meta?.extension?.find((ext: any) => ext.url === url)?.valueString || null;
    };

    const createdBy = getExtensionValue('createdBy') || 'N/A';
    const createdOn = getExtensionValue('createdOn') || tag.meta?.lastUpdated || '';
    const comments = getExtensionValue('comments') || '';
    
    // Extract resources from meta.tag - this would need to be implemented based on your data structure
    const resources: string[] = [];
    if (tag.meta?.tag) {
      tag.meta.tag.forEach((tagMeta: any) => {
        if (tagMeta.system && tagMeta.code) {
          resources.push(tagMeta.code);
        }
      });
    }

    return {
      display: firstConcept?.display || '',
      system,
      code: firstConcept?.code || '',
      codeSystem,
      version: tag.version || '',
      createdBy,
      createdOn,
      lastUpdated: tag.meta?.lastUpdated || '',
      description: tag.description || '',
      comments,
      resources,
    };
  }, [tag]);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      return moment(dateString).format('MMM DD, YYYY hh:mm A');
    } catch {
      return dateString;
    }
  };

  return (
    <PreviewDetailDrawer
      open={open}
      onClose={onClose}
      onCloseIconButtonClick={onClose}
      title="Details"
      containerSx={{ width: '25vw' }}
      shouldShowActionButton
      actionButtonTitle="Edit"
      actionButtonProps={{
        onClick: () => {
          onEdit(tagId);
          onClose();
        },
      }}
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : tagDetails ? (
        <Container>
          <SectionInfo title="General">
            <InfoRow label="Display" value={tagDetails.display} />
            <InfoRow label="System" value={tagDetails.system} />
            <InfoRow label="Code" value={tagDetails.code} />
            <InfoRow label="Code System" value={tagDetails.codeSystem} />
            <InfoRow label="Version" value={tagDetails.version || 'N/A'} />
            <InfoRow label="Created By" value={tagDetails.createdBy} />
            <InfoRow label="Created On" value={formatDate(tagDetails.createdOn)} />
            <InfoRow label="Last Updated" value={formatDate(tagDetails.lastUpdated)} />
            <InfoRow label="Description" value={tagDetails.description || 'N/A'} />
            <InfoRow label="Comments" value={tagDetails.comments || 'N/A'} />
            <InfoRow
              label="Resources Assigned"
              value={
                tagDetails.resources.length > 0
                  ? tagDetails.resources.join(', ')
                  : '-'
              }
            />
          </SectionInfo>
        </Container>
      ) : null}
    </PreviewDetailDrawer>
  );
}

